import { useState, useEffect } from 'react';
import {
  Form, LoaderFunctionArgs, useNavigation,
} from 'react-router-dom';

import { EmailDomainLookupResponse, OrganisationOption } from '../../api-models';
import EmailDomainLookup from '../../services/adapters/domainLookupRequest';
import useRequestState from '../../hooks/useRequestState';
import { validateAndExtractDomain } from '../../utils/formatters';
import getBackendUrl from '../../utils/backendUrl';

interface LoginState {
  step: 'email' | 'organisation';
  email: string;
  organisations: OrganisationOption[];
  selectedOrg: string;
  hasUserTyped: boolean;
}

interface LoginFormProps {
  sessionExpired?: boolean;
}

// eslint-disable-next-line react-refresh/only-export-components
export async function loginAction({ request }: LoaderFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get('email') as string | null;
  const selectedOrgSlug = formData.get('selected-organisation') as string | null;
  const step = formData.get('step') as string | null;

  if (step === 'email') {
    if (!email) {
      return {
        error: 'You must enter an email address to continue',
      };
    }

    try {
      const { isValid, domain, error } = validateAndExtractDomain(email);
      if (!isValid || !domain) {
        return { error: error || 'Invalid email domain' };
      }

      const domainLookupAdapter = new EmailDomainLookup();
      const lookupResponse = await domainLookupAdapter.adaptResponse(domain);

      if (!lookupResponse.organisations || lookupResponse.organisations.length === 0) {
        return {
          error: 'No organisation found for this email domain. Please contact your admin team for assistance.',
        };
      }

      // If only one organisation, redirect immediately
      if (lookupResponse.organisations.length === 1) {
        const org = lookupResponse.organisations[0];
        window.location.href = `${getBackendUrl()}/backend/auth/${org.slug}`;
      }

      // Multiple organisations found, show selection
      return {
        step: 'organisation',
        email,
        organisations: lookupResponse.organisations,
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Domain lookup error:', error);
      return {
        error: 'Unable to verify email domain. Please try again.',
      };
    }
  }

  if (step === 'organisation') {
    if (!selectedOrgSlug) {
      return {
        error: 'Please select an organisation to continue',
      };
    }

    try {
      window.location.href = `${getBackendUrl()}/backend/auth/${selectedOrgSlug}`;
    } catch (error) {
      return {
        error: 'Unable to proceed with login. Please try again.',
      };
    }
  }

  return {
    error: 'Invalid request',
  };
}

export default function LoginForm({ sessionExpired }: LoginFormProps) {
  const [loginState, setLoginState] = useState<LoginState>({
    step: 'email',
    email: '',
    organisations: [],
    selectedOrg: '',
    hasUserTyped: false,
  });

  const navigation = useNavigation();
  const {
    data: domainLookupData,
    error: domainError,
    handleRequest,
    FallbackWrapper,
  } = useRequestState<EmailDomainLookupResponse>();

  const isSubmitting = navigation.state === 'submitting';

  useEffect(() => {
    if (domainLookupData?.organisations) {
      if (domainLookupData?.organisations.length === 1) {
        // Single organisation - redirect immediately
        const org = domainLookupData.organisations[0];
        window.location.href = `${getBackendUrl()}/backend/auth/${org.slug}`;
      } else {
        // Multiple organisations - show selection step
        setLoginState((prev) => ({
          ...prev,
          step: 'organisation',
          hasUserTyped: false,
        }));
      }
    }
  }, [domainLookupData]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { isValid, domain } = validateAndExtractDomain(loginState.email);
    if (!isValid || !domain) {
      return;
    }

    const domainLookupAdapter = new EmailDomainLookup();
    const lookupRequest = domainLookupAdapter.adaptResponse(domain!);
    await handleRequest(lookupRequest);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;

    setLoginState((prev) => ({
      ...prev,
      email: emailValue,
      hasUserTyped: true,
    }));
  };

  const handleOrgSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginState.selectedOrg) {
      return;
    }

    window.location.href = `${getBackendUrl()}/backend/auth/${loginState.selectedOrg}`;
  };

  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLoginState((prev) => ({
      ...prev,
      selectedOrg: e.target.value,
      hasUserTyped: true,
    }));
  };

  const handleBackToEmail = () => {
    setLoginState((prev) => ({
      ...prev,
      step: 'email',
      selectedOrg: '',
      hasUserTyped: false,
    }));
  };

  const shouldShowError = (domainError && !loginState.hasUserTyped)
    || (domainLookupData?.organisations?.length === 0 && !loginState.hasUserTyped);

  const getErrorMessage = () => {
    if (domainLookupData?.organisations?.length === 0) {
      return 'No organisation found for this email domain. Please contact your admin team for assistance.';
    }
    return domainError?.message || 'Unable to verify email domain. Please try again.';
  };

  if (loginState.step === 'organisation' && domainLookupData?.organisations) {
    return (
      <div className="w-96 items-center justify-center flex flex-col rounded-xs">
        <Form onSubmit={handleOrgSubmit} className="w-full">
          <label htmlFor="selected-organisation" className="block">
            <select
              id="selected-organisation"
              name="selected-organisation"
              className="text-piptext-body text-center m-1 p-3 w-full h-12 text-lg rounded-md border border-pipgrey-light"
              value={loginState.selectedOrg}
              onChange={handleOrgChange}
              required
            >
              <option value="" disabled>Select your organisation</option>
              {domainLookupData.organisations.map((org) => (
                <option key={org.slug} value={org.slug}>
                  {org.name}
                </option>
              ))}
            </select>
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleBackToEmail}
              className="bg-gray-100 m-1 p-1 w-1/2 h-12 text-lg rounded-md border border-pipgrey-light hover:shadow-md flex items-center justify-center"
            >
              Back
            </button>
            <button
              id="login-button"
              type="submit"
              disabled={isSubmitting || !loginState.selectedOrg}
              className="bg-white m-1 p-1 w-1/2 h-12 text-lg rounded-md border border-pipgrey-light
                hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Logging in...
                </span>
              ) : 'Continue'}
            </button>
          </div>

          {shouldShowError && (
            <p id="login-error-message" className="text-red-500 text-sm break-words">
              {getErrorMessage()}
            </p>
          )}
        </Form>
      </div>
    );
  }

  return (
    <div className="w-96 items-center justify-center flex flex-col rounded-xs">
      <Form onSubmit={handleEmailSubmit} className="w-full">
        {sessionExpired && (
          <p role="alert" className="text-sm text-center text-red-600 mb-2">
            Session expired due to inactivity. Please log in again.
          </p>
        )}

        <label htmlFor="email" className="block" aria-label="email">
          <input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email address"
            className="text-piptext-body text-center m-1 p-3 w-full h-12 text-lg rounded-md border border-pipgrey-light"
            autoComplete="email"
            value={loginState.email}
            onChange={handleEmailChange}
            required
          />
        </label>

        <FallbackWrapper loadingMessage="Looking up...">
          <button
            id="login-button"
            type="submit"
            disabled={!loginState.email.trim()}
            className="bg-white m-1 p-1 w-full h-12 text-lg rounded-md border border-pipgrey-light
              hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            Continue
          </button>
        </FallbackWrapper>

        {shouldShowError && (
          <p id="login-error-message" className="text-red-500 text-sm break-words">
            {getErrorMessage()}
          </p>
        )}
      </Form>
    </div>
  );
}
